/**
 * Quote Download Service
 * Located: frontend/src/features/quotes/services/downloadService.js
 */

import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";

const PDF_REQUEST_TIMEOUT_MS = 90000;

class QuoteDownloadService {
  getFilenameFromResponse(response, fallback) {
    const contentDisposition = response.headers.get("Content-Disposition");
    if (!contentDisposition) {
      return fallback;
    }

    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    return filenameMatch?.[1] || fallback;
  }

  async readErrorMessage(response, fallbackMessage) {
    try {
      const text = await response.clone().text();
      if (!text) {
        return fallbackMessage;
      }
      const payload = JSON.parse(text);
      return payload.error || payload.message || fallbackMessage;
    } catch (_) {
      return fallbackMessage;
    }
  }

  async getPdfBlob(response, fallbackMessage) {
    const contentType = response.headers.get("Content-Type") || "";
    const responseForError = response.clone();
    const blob = await response.blob();
    const signature = await blob.slice(0, 5).text();

    if (!contentType.includes("application/pdf") || signature !== "%PDF-") {
      throw new Error(
        await this.readErrorMessage(responseForError, fallbackMessage)
      );
    }

    return blob;
  }

  /**
   * Make a binary request using the shared API's authentication and error handling
   * @param {string} url - Request URL
   * @param {Object} params - Query parameters
   * @param {string} acceptHeader - Accept header for the request
   * @returns {Promise<Response>} Fetch response
   */
  async makeBinaryRequest(
    url,
    params = {},
    acceptHeader = "application/pdf",
    options = {}
  ) {
    try {
      const response = await api.binary(url, params, acceptHeader, {
        timeout: PDF_REQUEST_TIMEOUT_MS,
        ...options,
      });
      return response;
    } catch (error) {
      logError("Binary request failed", {
        url,
        params,
        acceptHeader,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  async getPdfStatus(quoteId) {
    return api.get(`/quotes/${quoteId}/pdf/status`);
  }

  /** Synchronously generate quote PDF on the server */
  async generatePdf(quoteId, { force = false } = {}) {
    return api.post(
      `/quotes/${quoteId}/pdf/generate`,
      { reason: "manual_generate", force },
      { timeout: PDF_REQUEST_TIMEOUT_MS }
    );
  }

  /**
   * Download quote PDF (generates synchronously on the server if needed)
   * @param {string} quoteId - Quote ID
   * @returns {Promise<void>}
   */
  async downloadPdf(quoteId) {
    try {
      const url = `/quotes/${quoteId}/pdf`;

      const response = await this.makeBinaryRequest(url, {}, "application/pdf");

      const filename = this.getFilenameFromResponse(
        response,
        `quote_${quoteId || "unknown"}_summary.pdf`
      );

      const blob = await this.getPdfBlob(
        response,
        "Quote download did not return a valid PDF."
      );
      this.downloadBlob(blob, filename);
    } catch (error) {
      logError("PDF download failed", {
        quoteId,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Download items list PDF
   * @param {string} quoteId - Quote ID
   * @returns {Promise<void>}
   */
  async downloadItemsList(quoteId) {
    try {
      const url = `/quotes/${quoteId}/items/pdf`;

      const response = await this.makeBinaryRequest(url, {}, "application/pdf");

      const filename = this.getFilenameFromResponse(
        response,
        `quote_${quoteId || "unknown"}_items_list.pdf`
      );

      const blob = await this.getPdfBlob(
        response,
        "Items list download did not return a valid PDF."
      );
      this.downloadBlob(blob, filename);
    } catch (error) {
      logError("Items list PDF download failed", {
        quoteId,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Download blob as file
   * @param {Blob} blob - Blob to download
   * @param {string} filename - Filename for download
   */
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const quoteDownloadService = new QuoteDownloadService();
